<template>
  <div class="panel-card full-height" :class="{ loading: loading }">
    <div class="loading-overlay" v-if="loading"></div>
    <div class="page-header">
      <h3 class="page-header-title">已绑定人员列表</h3>
      <div class="page-header-actions">
        <div class="list-top">
          <el-input
            v-model.trim="filters.keyword"
            clearable
            size="small"
            class="personnel-search-input"
            placeholder="搜索人员昵称/微信ID/手机/设备ID"
            :disabled="loading"
            @keyup.enter.native="handleKeywordSearch"
            @clear="handleKeywordSearch"
          />
          <el-button
            size="small"
            class="search-btn"
            :disabled="loading"
            @click="handleKeywordSearch"
          >
            搜索
          </el-button>
          <el-select
            v-if="isMobileViewport"
            v-model="filters.department_id"
            clearable
            filterable
            placeholder="全部部门"
            size="small"
            :disabled="loading || filterOptionsLoading"
            @change="handleFilterChange"
          >
            <el-option :value="null" label="全部部门"></el-option>
            <el-option
              v-for="item in departmentOptions"
              :key="item.id"
              :label="item.label || item.name || `部门#${item.id}`"
              :value="item.id"
            ></el-option>
          </el-select>
          <el-select
            v-model="filters.online_status"
            placeholder="在线状态"
            size="small"
            :disabled="loading"
            @change="handleFilterChange"
          >
            <el-option
              v-for="item in onlineStatusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            ></el-option>
          </el-select>
        </div>
        <button class="btn p xs" :disabled="loading" @click="openCreateDialog">
          <span>新增人员</span>
        </button>
        <div v-if="canManageAiSwitch" class="batch-ai-actions">
          <span class="batch-ai-count">已选 {{ selectedPersonnelCount }} 项</span>
          <button
            class="btn s xs"
            :disabled="loading || batchAiSwitchLoading || aiScheduleSaving || !selectedPersonnelCount"
            @click="openAiScheduleDialog"
          >
            <span>{{ aiScheduleSaving ? "处理中" : "定时设置" }}</span>
          </button>
          <button
            class="btn p xs"
            :disabled="loading || batchAiSwitchLoading || !selectedPersonnelCount"
            @click="batchTogglePersonnelAi(1)"
          >
            <span>{{ batchAiSwitchLoading ? "处理中" : "批量开启AI" }}</span>
          </button>
          <button
            class="btn w xs"
            :disabled="loading || batchAiSwitchLoading || !selectedPersonnelCount"
            @click="batchTogglePersonnelAi(0)"
          >
            <span>{{ batchAiSwitchLoading ? "处理中" : "批量关闭AI" }}</span>
          </button>
          <button
            class="btn s xs"
            :disabled="loading || batchAiSwitchLoading || !selectedPersonnelCount"
            @click="clearPersonnelSelection"
          >
            <span>清空选择</span>
          </button>
        </div>
        <el-tooltip content="功能正在开发中" placement="top" effect="dark">
          <button class="btn p xs">
            <span>绑定企业微信</span>
          </button>
        </el-tooltip>
      </div>
    </div>

    <div class="table-shell full-height">
      <div v-if="isMobileViewport" class="mobile-personnel-list">
        <div class="mobile-personnel-head">
          <div class="mobile-personnel-head-cell mobile-personnel-head-name">
            <el-checkbox
              v-if="canManageAiSwitch"
              :value="isAllMobilePersonnelSelected"
              :indeterminate="isMobilePersonnelSelectionIndeterminate"
              :disabled="loading || batchAiSwitchLoading || !mobileSelectablePersonnelIds.length"
              @change="toggleAllMobilePersonnelSelection"
            ></el-checkbox>
            <span>昵称</span>
          </div>
          <span class="mobile-personnel-head-cell">操作</span>
        </div>
        <div v-if="!list.length" class="mobile-personnel-empty">暂无人员记录</div>
        <div v-for="row in list" v-else :key="row.id" class="mobile-personnel-item">
          <div class="mobile-personnel-name">
            <el-checkbox
              v-if="canManageAiSwitch"
              :value="isPersonnelSelected(row)"
              :disabled="loading || batchAiSwitchLoading"
              @change="toggleMobilePersonnelSelection(row, $event)"
            ></el-checkbox>
            <div class="mobile-personnel-summary">
              <div class="mobile-personnel-nickname">{{ row.nickname || "-" }}</div>
              <el-tag
                class="mobile-personnel-status"
                :type="resolveOnlineStatusTagType(row)"
                size="mini"
              >
                {{ resolveOnlineStatusLabel(row) }}
              </el-tag>
            </div>
          </div>
          <div class="mobile-personnel-actions">
            <span class="op-actions mobile">
              <button
                v-if="canManageAiSwitch"
                :class="['btn', 'xs', isPersonnelAiEnabled(row) ? 'w' : 'p']"
                :disabled="loading || batchAiSwitchLoading || !!aiSwitchLoadingMap[row.id]"
                @click="togglePersonnelAi(row)"
              >
                <span>{{ isPersonnelAiEnabled(row) ? "关闭AI" : "开启AI" }}</span>
              </button>
              <button class="btn p xs" @click="openPasswordDialog(row)">
                <span>编辑密码</span>
              </button>
              <button
                class="btn w xs"
                :disabled="removeLoading && removingPersonnelId === Number(row.id || 0)"
                @click="remove(row)"
              >
                <span>{{ removeLoading && removingPersonnelId === Number(row.id || 0) ? "删除中" : "删除人员" }}</span>
              </button>
              <button v-if="isAdmin" class="btn s xs" @click="openAiLogDialog(row)">
                <span>开关记录</span>
              </button>
            </span>
          </div>
        </div>
      </div>
      <div v-else class="desktop-personnel-shell">
        <aside class="department-tree-panel">
          <div class="department-tree-title">部门筛选</div>
          <el-tree
            ref="departmentTree"
            :data="departmentTreeData"
            node-key="id"
            :props="departmentTreeProps"
            :current-node-key="currentDepartmentTreeKey"
            highlight-current
            default-expand-all
            :expand-on-click-node="false"
            @node-click="handleDepartmentTreeClick"
          >
            <span slot-scope="{ data }" class="department-tree-node">
              <span class="department-tree-node-label">{{ data.label }}</span>
            </span>
          </el-tree>
        </aside>

        <div class="desktop-personnel-main">
          <el-table
            ref="personnelTable"
            :data="list"
            v-loading="loading"
            height="calc(100% - 56px)"
            @selection-change="handleTableSelectionChange"
          >
            <el-table-column
              v-if="canManageAiSwitch"
              type="selection"
              width="48"
              :selectable="isPersonnelSelectable"
            ></el-table-column>
            <el-table-column type="index" label="序号" width="70"></el-table-column>
            <el-table-column prop="nickname" label="昵称"></el-table-column>
            <el-table-column prop="wechat_id" label="微信ID"></el-table-column>
            <el-table-column prop="company_name" label="公司名称"></el-table-column>
            <el-table-column prop="login_time" label="登录时间"></el-table-column>
            <el-table-column prop="device_id" label="设备ID"></el-table-column>
            <el-table-column label="在线状态" width="100">
              <template slot-scope="{ row }">
                <el-tag :type="resolveOnlineStatusTagType(row)" size="small">
                  {{ resolveOnlineStatusLabel(row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="520">
              <template slot-scope="{ row }">
                <span class="op-actions">
                  <button class="btn s xs" @click="$router.push(`/personnel/${row.id}`)">详情</button>
                  <button
                    class="btn w xs"
                    :disabled="removeLoading && removingPersonnelId === Number(row.id || 0)"
                    @click="remove(row)"
                  >
                    <span>{{ removeLoading && removingPersonnelId === Number(row.id || 0) ? "删除中" : "删除" }}</span>
                  </button>
                  <button class="btn e xs" @click="openEditDialog(row)">
                    <span>编辑</span>
                  </button>
                  <button class="btn p xs" @click="openPasswordDialog(row)">
                    <span>编辑密码</span>
                  </button>
                  <button v-if="isAdmin" class="btn s xs" @click="openAiLogDialog(row)">
                    <span>开关记录</span>
                  </button>
                  <button
                    v-if="canManageAiSwitch"
                    :class="['btn', 'xs', isPersonnelAiEnabled(row) ? 'w' : 'p']"
                    :disabled="loading || batchAiSwitchLoading || !!aiSwitchLoadingMap[row.id]"
                    @click="togglePersonnelAi(row)"
                  >
                    <span>{{ isPersonnelAiEnabled(row) ? "关闭AI" : "开启AI" }}</span>
                  </button>
                </span>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-container">
            <div class="total-count">共 {{ pagination.total }} 条记录</div>
            <el-pagination
              background
              layout="sizes, prev, pager, next, jumper"
              :total="pagination.total"
              :page-size="pagination.per_page"
              :page-sizes="[10, 20, 50, 100]"
              :current-page="pagination.current_page"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              class="pagination"
            />
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      title="企业微信绑定"
      :visible.sync="bindVisible"
      width="420px"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
    >
      <div class="qrcode-wrap" :class="{ loading: qrLoading }">
        <div class="loading-overlay" v-if="qrLoading"></div>
        <img :src="qrUrl" alt="二维码" class="qrcode-img" />
        <p>请使用企业微信扫码绑定</p>
      </div>
    </el-dialog>

    <el-dialog
      :title="editMode === 'create' ? '新增人员' : '编辑人员信息'"
      :visible.sync="editVisible"
      width="500px"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
    >
      <el-form :model="editForm" label-width="100px">
        <el-form-item label="昵称">
          <el-input v-model="editForm.nickname" placeholder="请输入昵称"></el-input>
        </el-form-item>
        <el-form-item label="微信ID">
          <el-input v-model="editForm.wechat_id" placeholder="请输入微信ID"></el-input>
        </el-form-item>
        <el-form-item label="设备ID">
          <el-input v-model="editForm.device_id" placeholder="请输入设备ID"></el-input>
        </el-form-item>
        <el-form-item label="手机号码">
          <el-input v-model="editForm.phone" placeholder="请输入手机号码"></el-input>
        </el-form-item>
        <el-form-item label="绑定个人微信">
          <div class="personal-wechat-row">
            <el-input v-model="editForm.personal_wechat_id" placeholder="请输入个人微信ID"></el-input>
            <el-button
              v-if="editMode === 'edit'"
              type="primary"
              plain
              size="mini"
              :loading="sendTestLoading"
              :disabled="!String(editForm.personal_wechat_id || '').trim()"
              @click="sendEditPersonalWechatTestMessage"
            >
              发送测试信息
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="设备Token">
          <el-input v-model="editForm.device_token" placeholder="请输入设备Token"></el-input>
        </el-form-item>
        <el-form-item label="公司名称">
          <el-input v-model="editForm.company_name" placeholder="请输入公司名称"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" @click="submitPersonnel" :loading="editLoading">
          {{ editMode === 'create' ? '新增' : '保存' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="编辑登录密码"
      :visible.sync="passwordVisible"
      width="460px"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
    >
      <el-form :model="passwordForm" label-width="100px">
        <el-form-item label="人员">
          <el-input v-model="passwordForm.nickname" disabled></el-input>
        </el-form-item>
        <el-form-item label="当前密码">
          <el-input v-model="passwordForm.current_password" readonly></el-input>
        </el-form-item>
        <el-form-item label="新密码">
          <el-input
            v-model.trim="passwordForm.new_password"
            type="password"
            show-password
            placeholder="请输入6-64位新密码"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="passwordVisible = false">取消</el-button>
        <el-button type="primary" @click="updatePassword" :loading="passwordLoading">保存密码</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="AI定时设置"
      :visible.sync="aiScheduleVisible"
      width="460px"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
    >
      <el-form :model="aiScheduleForm" label-width="100px">
        <el-form-item label="已选人员">
          <div class="schedule-selected-summary">
            已选 {{ selectedPersonnelCount }} 人
            <span v-if="aiScheduleMixed" class="schedule-selected-tip">当前存在不同设置，保存后会统一覆盖</span>
          </div>
        </el-form-item>
        <el-form-item label="启用定时">
          <el-switch
            v-model="aiScheduleForm.is_active"
            :active-value="1"
            :inactive-value="0"
          ></el-switch>
        </el-form-item>
        <el-form-item label="开启时间">
          <div class="schedule-time-row">
            <el-time-picker
              v-model="aiScheduleForm.open_time"
              value-format="HH:mm"
              format="HH:mm"
              placeholder="请选择开启时间"
              :disabled="aiScheduleForm.is_active !== 1"
            ></el-time-picker>
            <el-button
              plain
              size="mini"
              :disabled="aiScheduleForm.is_active !== 1 || !aiScheduleForm.open_time"
              @click="clearAiScheduleTime('open_time')"
            >
              删除
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="关闭时间">
          <div class="schedule-time-row">
            <el-time-picker
              v-model="aiScheduleForm.close_time"
              value-format="HH:mm"
              format="HH:mm"
              placeholder="请选择关闭时间"
              :disabled="aiScheduleForm.is_active !== 1"
            ></el-time-picker>
            <el-button
              plain
              size="mini"
              :disabled="aiScheduleForm.is_active !== 1 || !aiScheduleForm.close_time"
              @click="clearAiScheduleTime('close_time')"
            >
              删除
            </el-button>
          </div>
        </el-form-item>
        <div class="schedule-dialog-note">
          不设置时间就是纯手动。手动切换优先，手动改过后会暂停定时；重新保存定时后才会恢复自动切换。
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="aiScheduleVisible = false">取消</el-button>
        <el-button type="primary" :loading="aiScheduleSaving" @click="saveAiSchedule">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="`${aiLogTargetName} 开关记录`"
      :visible.sync="aiLogVisible"
      :width="isMobileViewport ? '100%' : '760px'"
      :fullscreen="isMobileViewport"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
      :custom-class="isMobileViewport ? 'ai-log-mobile-dialog' : 'ai-log-desktop-dialog'"
    >
      <div class="ai-log-dialog" v-loading="aiLogLoading">
        <el-tabs v-model="aiLogActiveTab">
          <el-tab-pane label="自己AI开关记录" name="personnel">
            <div v-if="!aiLogs.personnel.length" class="ai-log-empty">暂无记录</div>
            <div v-else class="ai-log-list">
              <div v-for="item in aiLogs.personnel" :key="`personnel_${item.id}`" class="ai-log-item">
                <div class="ai-log-main">
                  <span class="ai-log-target">{{ item.target_name || aiLogTargetName }}</span>
                  <el-tag :type="item.enabled === 1 ? 'success' : 'info'" size="mini">{{ item.action_label }}</el-tag>
                </div>
                <div class="ai-log-meta">
                  <span>操作人：{{ item.operator_name || '-' }}</span>
                  <span>{{ item.created_at || '-' }}</span>
                </div>
              </div>
            </div>
          </el-tab-pane>
          <el-tab-pane label="客户AI开关记录" name="customer">
            <div v-if="!aiLogs.customer.length" class="ai-log-empty">暂无记录</div>
            <div v-else class="ai-log-list">
              <div v-for="item in aiLogs.customer" :key="`customer_${item.id}`" class="ai-log-item">
                <div class="ai-log-main">
                  <span class="ai-log-target">{{ item.target_name || '-' }}</span>
                  <el-tag :type="item.enabled === 1 ? 'success' : 'info'" size="mini">{{ item.action_label }}</el-tag>
                </div>
                <div class="ai-log-meta">
                  <span>操作人：{{ item.operator_name || '-' }}</span>
                  <span>{{ item.created_at || '-' }}</span>
                </div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import { personnelApi } from "../../api";

const DEFAULT_DEVICE_TOKEN = "3d271310-cc16-48ca-8587-23e01bcbc802";
const DEFAULT_COMPANY_NAME = "勉之律所";

function createDefaultEditForm() {
  return {
    id: "",
    nickname: "",
    wechat_id: "",
    device_id: "",
    phone: "",
    personal_wechat_id: "",
    device_token: DEFAULT_DEVICE_TOKEN,
    company_name: DEFAULT_COMPANY_NAME
  };
}

export default {
  data() {
    return {
      list: [],
      isMobileViewport: false,
      bindVisible: false,
      editVisible: false,
      editMode: "edit",
      passwordVisible: false,
      aiScheduleVisible: false,
      aiLogVisible: false,
      aiScheduleSaving: false,
      aiScheduleMixed: false,
      aiLogLoading: false,
      aiLogActiveTab: "personnel",
      aiLogTargetRow: null,
      editForm: createDefaultEditForm(),
      passwordForm: {
        id: '',
        nickname: '',
        current_password: '',
        new_password: ''
      },
      aiScheduleForm: {
        is_active: 1,
        open_time: "09:00",
        close_time: "18:00"
      },
      qrUrl: "https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=mianzhi-bind-demo",
      loading: false,
      qrLoading: false,
      removeLoading: false,
      removingPersonnelId: 0,
      editLoading: false,
      sendTestLoading: false,
      passwordLoading: false,
      currentPage: 1,
      filterOptionsLoading: false,
      batchAiSwitchLoading: false,
      departmentOptions: [],
      departmentTreeKeySeed: 0,
      onlineStatusOptions: [
        { value: "online", label: "在线" },
        { value: "offline", label: "离线" },
        { value: "unbound", label: "未绑定" },
        { value: "all", label: "全部" }
      ],
      filters: {
        keyword: "",
        department_id: null,
        online_status: "all"
      },
      aiLogs: {
        personnel: [],
        customer: []
      },
      selectedPersonnelIds: [],
      aiSwitchLoadingMap: {},
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1
      }
    };
  },
  computed: {
    isAdmin() {
      if (localStorage.getItem("is_admin") === "1") return true;
      const username = String(localStorage.getItem("username") || "").trim().toLowerCase();
      if (username === "admin@mianzhi.com" || username === "admin") return true;
      try {
        const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
        return Number(user?.is_admin) === 1 || user?.is_admin === true;
      } catch (e) {
        return false;
      }
    },
    canManageAiSwitch() {
      if (this.isAdmin) return true;
      const allowNames = new Set(["吴强", "刘方韬", "郭晓红"]);
      const username = String(localStorage.getItem("username") || "").trim();
      if (allowNames.has(username)) return true;
      try {
        const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
        return allowNames.has(String(user?.name || "").trim());
      } catch (e) {
        return false;
      }
    },
    aiLogTargetName() {
      const nickname = String(this.aiLogTargetRow?.nickname || "").trim();
      if (nickname) return nickname;
      const wechatId = String(this.aiLogTargetRow?.wechat_id || "").trim();
      if (wechatId) return wechatId;
      const id = Number(this.aiLogTargetRow?.id || 0);
      return id > 0 ? `人员#${id}` : "AI";
    },
    selectedPersonnelCount() {
      return this.selectedPersonnelIds.length;
    },
    selectedPersonnelRows() {
      const selectedIdSet = new Set(this.selectedPersonnelIds.map((id) => Number(id || 0)).filter((id) => id > 0));
      return this.list.filter((row) => selectedIdSet.has(Number(row?.id || 0)));
    },
    mobileSelectablePersonnelIds() {
      return this.list
        .map((row) => Number(row?.id || 0))
        .filter((id) => id > 0);
    },
    isAllMobilePersonnelSelected() {
      const ids = this.mobileSelectablePersonnelIds;
      if (!ids.length) return false;
      return ids.every((id) => this.selectedPersonnelIds.includes(id));
    },
    isMobilePersonnelSelectionIndeterminate() {
      const ids = this.mobileSelectablePersonnelIds;
      if (!ids.length) return false;
      const selectedCount = ids.filter((id) => this.selectedPersonnelIds.includes(id)).length;
      return selectedCount > 0 && selectedCount < ids.length;
    },
    currentDepartmentTreeKey() {
      this.departmentTreeKeySeed;
      return Number(this.filters.department_id || 0);
    },
    departmentTreeProps() {
      return {
        label: "label",
        children: "children"
      };
    },
    departmentTreeData() {
      const childrenByParent = new Map();
      const nodesById = new Map();

      this.departmentOptions.forEach((item) => {
        const id = Number(item?.id || 0);
        if (id <= 0) return;

        const level = Number(item?.level || 1);
        const parentId = Number(item?.parent_id || 0);
        nodesById.set(id, {
          id,
          label: String(item?.name || item?.label || `部门#${id}`),
          parent_id: parentId > 0 ? parentId : null,
          level,
          children: []
        });
      });

      nodesById.forEach((node) => {
        const parentId = Number(node.parent_id || 0);
        if (parentId > 0 && nodesById.has(parentId)) {
          if (!childrenByParent.has(parentId)) {
            childrenByParent.set(parentId, []);
          }
          childrenByParent.get(parentId).push(node.id);
        }
      });

      nodesById.forEach((node) => {
        const childIds = childrenByParent.get(node.id) || [];
        node.children = childIds
          .map((childId) => nodesById.get(childId))
          .filter(Boolean)
          .sort((a, b) => {
            if (a.level !== b.level) return a.level - b.level;
            return a.id - b.id;
          });
      });

      const roots = Array.from(nodesById.values())
        .filter((node) => !node.parent_id || !nodesById.has(node.parent_id))
        .sort((a, b) => a.id - b.id);

      return [{
        id: 0,
        label: "全部部门",
        children: roots
      }];
    }
  },
  created() {
    this.updateViewportMode();
    this.loadFilterOptions();
    this.loadList();
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.updateViewportMode, { passive: true });
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.updateViewportMode);
    }
  },
  methods: {
    updateViewportMode() {
      if (typeof window === "undefined") return;
      this.isMobileViewport = window.innerWidth <= 900;
    },
    hasDeviceId(row) {
      return String(row?.device_id || "").trim() !== "";
    },
    resolveAiSwitchValue(row) {
      const value = row && row.enable_ai_response !== undefined
        ? row.enable_ai_response
        : row?.personnel;
      return Number(value) === 1 ? 1 : 0;
    },
    isPersonnelAiEnabled(row) {
      return this.resolveAiSwitchValue(row) === 1;
    },
    isPersonnelSelected(row) {
      const id = Number(row?.id || 0);
      return id > 0 && this.selectedPersonnelIds.includes(id);
    },
    normalizeScheduleTimeValue(value) {
      const text = String(value || "").trim();
      if (!text) return "";
      const match = text.match(/^(\d{2}:\d{2})/);
      return match ? match[1] : "";
    },
    resolvePersonnelSchedule(row) {
      return {
        is_active: Number(row?.ai_schedule_enabled || 0) === 1 ? 1 : 0,
        open_time: this.normalizeScheduleTimeValue(row?.ai_schedule_open_time),
        close_time: this.normalizeScheduleTimeValue(row?.ai_schedule_close_time)
      };
    },
    buildSelectedScheduleSummary() {
      const rows = this.selectedPersonnelRows;
      if (!rows.length) {
        return {
          mixed: false,
          is_active: 1,
          open_time: "09:00",
          close_time: "18:00"
        };
      }

      const [firstRow, ...restRows] = rows;
      const firstSchedule = this.resolvePersonnelSchedule(firstRow);
      const mixed = restRows.some((row) => {
        const schedule = this.resolvePersonnelSchedule(row);
        return schedule.is_active !== firstSchedule.is_active
          || schedule.open_time !== firstSchedule.open_time
          || schedule.close_time !== firstSchedule.close_time;
      });

      return {
        mixed,
        is_active: mixed ? 1 : firstSchedule.is_active,
        open_time: mixed ? "" : firstSchedule.open_time,
        close_time: mixed ? "" : firstSchedule.close_time
      };
    },
    resolveOnlineStatus(row) {
      if (!this.hasDeviceId(row)) return "unbound";
      return String(row?.online_status || "").toLowerCase() === "online" ? "online" : "offline";
    },
    resolveOnlineStatusTagType(row) {
      const status = this.resolveOnlineStatus(row);
      if (status === "online") return "success";
      if (status === "offline") return "info";
      return "warning";
    },
    resolveOnlineStatusLabel(row) {
      const status = this.resolveOnlineStatus(row);
      if (status === "online") return "在线";
      if (status === "offline") return "离线";
      return "未绑定";
    },
    normalizePersonnelList(rows) {
      const source = Array.isArray(rows) ? rows : [];
      const normalized = source.map((row) => ({
        ...row,
        enable_ai_response: this.resolveAiSwitchValue(row),
        online_status: String(row?.online_status || "").toLowerCase() === "online" ? "online" : "offline",
        ai_schedule_enabled: Number(row?.ai_schedule_enabled || 0) === 1 ? 1 : 0,
        ai_schedule_open_time: this.normalizeScheduleTimeValue(row?.ai_schedule_open_time),
        ai_schedule_close_time: this.normalizeScheduleTimeValue(row?.ai_schedule_close_time)
      }));

      normalized.sort((a, b) => {
        const aHasDeviceId = this.hasDeviceId(a) ? 1 : 0;
        const bHasDeviceId = this.hasDeviceId(b) ? 1 : 0;
        if (aHasDeviceId !== bHasDeviceId) return bHasDeviceId - aHasDeviceId;

        const aOnline = a.online_status === "online" ? 1 : 0;
        const bOnline = b.online_status === "online" ? 1 : 0;
        if (aOnline !== bOnline) return bOnline - aOnline;

        return Number(b.id || 0) - Number(a.id || 0);
      });

      return normalized;
    },
    normalizeDepartmentOptions(options) {
      const source = Array.isArray(options) ? options : [];
      return source
        .map((item) => ({
          id: Number(item?.id || 0),
          name: String(item?.name || ""),
          label: String(item?.label || item?.name || ""),
          parent_id: Number(item?.parent_id || 0),
          level: Number(item?.level || 1)
        }))
        .filter((item) => item.id > 0);
    },
    async loadFilterOptions() {
      this.filterOptionsLoading = true;
      try {
        const data = await personnelApi.getPersonnelFilterOptions();
        this.departmentOptions = this.normalizeDepartmentOptions(data?.departments);

        const statusOptions = Array.isArray(data?.online_statuses) ? data.online_statuses : [];
        if (statusOptions.length) {
          const statusLabelMap = {
            online: "在线",
            offline: "离线",
            unbound: "未绑定",
            all: "全部"
          };
          const normalizedStatusOptions = statusOptions.map((item) => ({
            value: String(item?.value || ""),
            label: statusLabelMap[String(item?.value || "")] || String(item?.label || "")
          })).filter((item) => item.value && item.label);
          if (normalizedStatusOptions.length) {
            this.onlineStatusOptions = normalizedStatusOptions;
          }
        }
      } catch (e) {
        this.departmentOptions = [];
      } finally {
        this.filterOptionsLoading = false;
      }
    },
    handleFilterChange() {
      if (this.loading) return;
      this.pagination.current_page = 1;
      this.departmentTreeKeySeed += 1;
      this.loadList();
    },
    handleKeywordSearch() {
      if (this.loading) return;
      this.pagination.current_page = 1;
      this.loadList();
    },
    handleDepartmentTreeClick(node) {
      const departmentId = Number(node?.id || 0);
      this.filters.department_id = departmentId > 0 ? departmentId : null;
      this.handleFilterChange();
    },
    async openAiLogDialog(row) {
      if (!this.isAdmin || !row?.id) return;

      this.aiLogTargetRow = row;
      this.aiLogVisible = true;
      this.aiLogActiveTab = "personnel";
      this.aiLogLoading = true;
      this.aiLogs = {
        personnel: [],
        customer: []
      };

      try {
        const data = await personnelApi.getPersonnelAiSwitchLogs(row.id, { limit: 100 });
        this.aiLogs = {
          personnel: Array.isArray(data?.personnel_ai_logs) ? data.personnel_ai_logs : [],
          customer: Array.isArray(data?.customer_ai_logs) ? data.customer_ai_logs : []
        };
      } catch (e) {
        this.$message.warning("加载开关记录失败");
      } finally {
        this.aiLogLoading = false;
      }
    },
    async togglePersonnelAi(row) {
      if (!this.canManageAiSwitch) {
        this.$message.warning("仅 admin、吴强、刘方韬、郭晓红 可操作AI开关");
        return;
      }
      const id = Number(row?.id || 0);
      if (!id || this.aiSwitchLoadingMap[id]) return;

      const nextValue = this.isPersonnelAiEnabled(row) ? 0 : 1;
      this.$set(this.aiSwitchLoadingMap, id, true);
      try {
        const data = await personnelApi.updatePersonnelAiSwitch(id, {
          enable_ai_response: nextValue
        });
        const enabled = data && data.enable_ai_response !== undefined
          ? (Number(data.enable_ai_response) === 1 ? 1 : 0)
          : nextValue;
        this.$set(row, "enable_ai_response", enabled);
        this.$message.success(enabled === 1 ? "AI已开启" : "AI已关闭");
      } catch (e) {
        this.$message.warning("AI开关更新失败");
      } finally {
        this.$delete(this.aiSwitchLoadingMap, id);
      }
    },
    isPersonnelSelectable() {
      return !this.batchAiSwitchLoading;
    },
    handleTableSelectionChange(rows) {
      this.selectedPersonnelIds = Array.isArray(rows)
        ? rows.map((row) => Number(row?.id || 0)).filter((id) => id > 0)
        : [];
    },
    toggleMobilePersonnelSelection(row, checked) {
      const id = Number(row?.id || 0);
      if (!id) return;
      const selected = !!checked;
      if (selected) {
        if (!this.selectedPersonnelIds.includes(id)) {
          this.selectedPersonnelIds = [...this.selectedPersonnelIds, id];
        }
        return;
      }
      this.selectedPersonnelIds = this.selectedPersonnelIds.filter((item) => item !== id);
    },
    toggleAllMobilePersonnelSelection(checked) {
      const ids = this.mobileSelectablePersonnelIds;
      if (!ids.length) return;

      if (checked) {
        this.selectedPersonnelIds = Array.from(new Set([
          ...this.selectedPersonnelIds,
          ...ids
        ]));
        return;
      }

      const idSet = new Set(ids);
      this.selectedPersonnelIds = this.selectedPersonnelIds.filter((id) => !idSet.has(id));
    },
    clearPersonnelSelection() {
      this.selectedPersonnelIds = [];
      if (this.$refs.personnelTable && typeof this.$refs.personnelTable.clearSelection === "function") {
        this.$refs.personnelTable.clearSelection();
      }
    },
    openAiScheduleDialog() {
      if (!this.canManageAiSwitch) {
        this.$message.warning("仅 admin、吴强、刘方韬、郭晓红 可操作AI开关");
        return;
      }
      if (!this.selectedPersonnelCount) {
        this.$message.warning("请先选择人员");
        return;
      }

      const summary = this.buildSelectedScheduleSummary();
      this.aiScheduleMixed = !!summary.mixed;
      this.aiScheduleForm = {
        is_active: summary.is_active,
        open_time: summary.open_time,
        close_time: summary.close_time
      };
      this.aiScheduleVisible = true;
    },
    clearAiScheduleTime(field) {
      if (!["open_time", "close_time"].includes(field)) return;
      this.$set(this.aiScheduleForm, field, "");
      if (!this.aiScheduleForm.open_time && !this.aiScheduleForm.close_time) {
        this.aiScheduleForm.is_active = 0;
      }
    },
    async saveAiSchedule() {
      if (!this.selectedPersonnelCount) {
        this.$message.warning("请先选择人员");
        return;
      }

      const ids = this.selectedPersonnelIds
        .map((value) => Number(value || 0))
        .filter((value) => value > 0);
      const openTime = this.normalizeScheduleTimeValue(this.aiScheduleForm.open_time);
      const closeTime = this.normalizeScheduleTimeValue(this.aiScheduleForm.close_time);
      let isActive = Number(this.aiScheduleForm.is_active || 0) === 1 ? 1 : 0;

      if (!openTime && !closeTime) {
        isActive = 0;
      } else if (!openTime || !closeTime) {
        this.$message.warning("开启和关闭时间要么一起设置，要么一起删除");
        return;
      } else if (openTime === closeTime) {
        this.$message.warning("开启和关闭时间不能一样");
        return;
      } else if (isActive !== 1) {
        isActive = 0;
      }

      this.aiScheduleSaving = true;
      try {
        const data = await personnelApi.batchUpdatePersonnelAiSwitchSchedule({
          ids,
          is_active: isActive,
          open_time: isActive === 1 ? openTime : null,
          close_time: isActive === 1 ? closeTime : null
        });
        const updatedIds = new Set(
          (Array.isArray(data?.ids) ? data.ids : ids)
            .map((value) => Number(value || 0))
            .filter((value) => value > 0)
        );

        this.list = this.list.map((row) => {
          const id = Number(row?.id || 0);
          if (!updatedIds.has(id)) return row;
          return {
            ...row,
            ai_schedule_enabled: isActive,
            ai_schedule_open_time: isActive === 1 ? openTime : "",
            ai_schedule_close_time: isActive === 1 ? closeTime : ""
          };
        });

        this.aiScheduleMixed = false;
        this.aiScheduleVisible = false;
        this.$message.success(isActive === 1 ? "AI定时设置已保存" : "AI定时已关闭");
      } catch (e) {
        this.$message.warning("保存AI定时设置失败");
      } finally {
        this.aiScheduleSaving = false;
      }
    },
    async batchTogglePersonnelAi(enabled) {
      if (!this.canManageAiSwitch) {
        this.$message.warning("仅 admin、吴强、刘方韬、郭晓红 可操作AI开关");
        return;
      }
      const ids = this.selectedPersonnelIds
        .map((value) => Number(value || 0))
        .filter((value) => value > 0);
      if (!ids.length) {
        this.$message.warning("请先选择人员");
        return;
      }

      this.batchAiSwitchLoading = true;
      try {
        const data = await personnelApi.batchUpdatePersonnelAiSwitch({
          ids,
          enable_ai_response: enabled
        });
        const updatedIds = new Set(
          (Array.isArray(data?.ids) ? data.ids : ids)
            .map((value) => Number(value || 0))
            .filter((value) => value > 0)
        );
        this.list = this.list.map((row) => {
          const id = Number(row?.id || 0);
          if (!updatedIds.has(id)) return row;
          return {
            ...row,
            enable_ai_response: enabled
          };
        });
        this.clearPersonnelSelection();
        const updatedCount = Number(data?.updated_count || updatedIds.size || ids.length);
        this.$message.success(`${updatedCount} 人已${enabled === 1 ? "开启" : "关闭"}AI`);
      } catch (e) {
        this.$message.warning("批量更新AI开关失败");
      } finally {
        this.batchAiSwitchLoading = false;
      }
    },
    async loadList() {
      this.loading = true;
      try {
        const data = await personnelApi.getPersonnelList({
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          keyword: String(this.filters.keyword || "").trim() || undefined,
          department_id: this.filters.department_id || undefined,
          online_status: this.filters.online_status || "all"
        });
        const rows = Array.isArray(data) ? data : data.items || [];
        this.list = this.normalizePersonnelList(rows);
        if (data && data.pagination) {
          this.pagination = {
            ...this.pagination,
            ...data.pagination
          };
        } else {
          this.pagination.total = this.list.length;
        }
        this.clearPersonnelSelection();
      } catch (e) {
        this.list = [];
        this.pagination.total = 0;
        this.clearPersonnelSelection();
        this.$message.error("加载人员列表失败");
      } finally {
        this.loading = false;
      }
    },
    openBindDialog() {
      this.$message.info("功能正在开发中");
    },
    async remove(row) {
      const id = Number(row?.id || 0);
      if (!id) {
        this.$message.warning("人员ID无效");
        return;
      }

      const name = String(row?.nickname || row?.wechat_id || `人员#${id}`).trim();

      try {
        await this.$confirm(`确认删除人员“${name}”吗？删除后不可恢复。`, "删除确认", {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "warning"
        });
      } catch (e) {
        return;
      }

      this.removeLoading = true;
      this.removingPersonnelId = id;
      try {
        await personnelApi.unbindPersonnel(id);
        this.$message.success("人员删除成功");

        if (this.list.length === 1 && Number(this.pagination.current_page || 1) > 1) {
          this.pagination.current_page -= 1;
        }

        await this.loadList();
      } catch (e) {
        this.$message.warning("删除人员失败，请稍后重试");
      } finally {
        this.removeLoading = false;
        this.removingPersonnelId = 0;
      }
    },
    openCreateDialog() {
      this.editMode = "create";
      this.editForm = createDefaultEditForm();
      this.sendTestLoading = false;
      this.editVisible = true;
    },
    openEditDialog(row) {
      this.editMode = "edit";
      this.editForm = {
        ...createDefaultEditForm(),
        id: row.id,
        nickname: row.nickname,
        wechat_id: row.wechat_id,
        device_id: row.device_id || "",
        phone: row.phone || "",
        personal_wechat_id: row.personal_wechat_id || "",
        device_token: row.device_token || DEFAULT_DEVICE_TOKEN,
        company_name: row.company_name || DEFAULT_COMPANY_NAME
      };
      this.sendTestLoading = false;
      this.editVisible = true;
    },
    async openPasswordDialog(row) {
      this.passwordVisible = true;
      this.passwordForm = {
        id: row?.id || '',
        nickname: row?.nickname || '',
        current_password: '',
        new_password: ''
      };
      this.passwordLoading = true;
      try {
        const data = await personnelApi.getPersonnelPassword(row.id);
        this.passwordForm = {
          ...this.passwordForm,
          id: data?.id || row?.id || '',
          nickname: data?.nickname || row?.nickname || '',
          current_password: data?.password || ''
        };
      } catch (e) {
        this.$message.warning("读取密码失败");
      } finally {
        this.passwordLoading = false;
      }
    },
    async updatePassword() {
      const id = Number(this.passwordForm.id || 0);
      const newPassword = String(this.passwordForm.new_password || "").trim();
      if (!id) return;
      if (newPassword.length < 6 || newPassword.length > 64) {
        this.$message.warning("新密码需为6-64位");
        return;
      }
      this.passwordLoading = true;
      try {
        const data = await personnelApi.updatePersonnelPassword(id, {
          password: newPassword
        });
        this.passwordForm = {
          ...this.passwordForm,
          current_password: data?.password || newPassword,
          new_password: ''
        };
        this.$message.success("密码更新成功");
      } catch (e) {
        this.$message.warning("密码更新失败");
      } finally {
        this.passwordLoading = false;
      }
    },
    async submitPersonnel() {
      const payload = {
        nickname: this.editForm.nickname,
        wechat_id: this.editForm.wechat_id,
        device_id: this.editForm.device_id,
        phone: this.editForm.phone,
        personal_wechat_id: this.editForm.personal_wechat_id,
        device_token: this.editForm.device_token || DEFAULT_DEVICE_TOKEN,
        company_name: this.editForm.company_name || DEFAULT_COMPANY_NAME
      };
      this.editLoading = true;
      try {
        if (this.editMode === "create") {
          await personnelApi.createPersonnel(payload);
          this.$message.success("新增成功");
        } else {
          await personnelApi.updatePersonnel(this.editForm.id, payload);
          this.$message.success("更新成功");
        }
        this.editVisible = false;
        this.editMode = "edit";
        this.loadList();
      } catch (e) {
        this.$message.warning(this.editMode === "create" ? "新增失败，请检查后端服务" : "更新失败，请检查后端服务");
      } finally {
        this.editLoading = false;
      }
    },
    async sendEditPersonalWechatTestMessage() {
      const id = Number(this.editForm.id || 0);
      const personalWechatId = String(this.editForm.personal_wechat_id || "").trim();
      if (!id) {
        this.$message.warning("人员ID无效，无法发送测试信息");
        return;
      }
      if (!personalWechatId) {
        this.$message.warning("请先填写绑定个人微信ID");
        return;
      }

      this.sendTestLoading = true;
      try {
        await personnelApi.sendPersonnelTestMessage(id, {
          personal_wechat_id: personalWechatId
        });
        this.$message.success("测试信息已发送，请到对应微信确认");
      } catch (e) {
        this.$message.warning("发送测试信息失败，请稍后重试");
      } finally {
        this.sendTestLoading = false;
      }
    },
    handleCurrentChange(val) {
      if (this.loading) return;
      this.pagination.current_page = val;
      this.loadList();
    },
    handleSizeChange(size) {
      if (this.loading) return;
      this.pagination.per_page = size;
      this.pagination.current_page = 1;
      this.loadList();
    }
  }
};
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.page-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.list-top {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  margin: 0;
  flex-wrap: wrap;
}

.list-top .el-select {
  min-width: 180px;
}

.personnel-search-input {
  width: 240px;
}

.batch-ai-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.batch-ai-count {
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}

.schedule-selected-summary {
  color: #334155;
  line-height: 1.6;
}

.schedule-selected-tip {
  display: inline-block;
  margin-left: 8px;
  color: #d97706;
  font-size: 12px;
}

.schedule-dialog-note {
  margin: 4px 0 0 100px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.schedule-time-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.el-time-picker) {
  width: 100%;
}

.personal-wechat-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.personal-wechat-row .el-input {
  flex: 1;
}

.ai-log-dialog {
  min-height: 220px;
}

:deep(.ai-log-mobile-dialog) {
  margin: 0 !important;
}

:deep(.ai-log-mobile-dialog .el-dialog__header) {
  padding: 14px 16px 10px;
  border-bottom: 1px solid #e2e8f0;
}

:deep(.ai-log-mobile-dialog .el-dialog__title) {
  display: block;
  padding-right: 24px;
  font-size: 16px;
  line-height: 1.4;
  word-break: break-word;
}

:deep(.ai-log-mobile-dialog .el-dialog__body) {
  padding: 12px;
  height: calc(100vh - 56px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

:deep(.ai-log-mobile-dialog .el-tabs__header) {
  margin-bottom: 10px;
}

:deep(.ai-log-mobile-dialog .el-tabs__nav-wrap) {
  overflow-x: auto;
  overflow-y: hidden;
}

:deep(.ai-log-mobile-dialog .el-tabs__nav-scroll) {
  min-width: max-content;
}

:deep(.ai-log-mobile-dialog .el-tabs__item) {
  height: auto;
  line-height: 1.35;
  padding: 0 10px 8px;
  white-space: nowrap;
}

.ai-log-list {
  display: grid;
  gap: 10px;
}

.ai-log-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  background: #f8fafc;
}

.ai-log-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ai-log-target {
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
}

.ai-log-meta {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #64748b;
  font-size: 12px;
  flex-wrap: wrap;
}

.ai-log-empty {
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  padding: 18px 12px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
}

.qrcode-wrap {
  text-align: center;
  background: linear-gradient(170deg, #f8fffd, #eff9f6);
  border: 1px solid #e4efea;
  border-radius: 13px;
  padding: 11px;
}

.qrcode-img {
  width: 220px;
  height: 220px;
  border: 1px solid #dcece6;
  border-radius: 12px;
  background: #fff;
  object-fit: contain;
  image-rendering: pixelated;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
}

.qrcode-wrap p {
  color: #64748b;
  margin: 10px 0 2px;
}

/* Loading styles */
.loading {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(2px);
}

.loading-overlay::after {
  content: '';
  width: 32px;
  height: 32px;
  border: 3px solid rgba(15, 118, 110, 0.1);
  border-top-color: #0f766e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Ensure loading overlay works correctly */
.panel-card {
  position: relative;
}

.qrcode-wrap {
  position: relative;
}

.qrcode-wrap .loading-overlay {
  border-radius: 13px;
}

/* Full height layout */
.full-height {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 130px);
  overflow: hidden;
}

.table-shell {
  flex: 1;
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.desktop-personnel-shell {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  gap: 12px;
  padding: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 32%);
}

.department-tree-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.department-tree-title {
  padding: 14px 16px 10px;
  border-bottom: 1px solid #eef2f7;
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
}

.department-tree-panel :deep(.el-tree) {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 8px 12px;
  color: #334155;
}

.department-tree-panel :deep(.el-tree-node__content) {
  height: auto;
  min-height: 34px;
  border-radius: 8px;
  margin: 2px 0;
  padding-right: 8px;
}

.department-tree-panel :deep(.el-tree-node__content:hover) {
  background: #f1f5f9;
}

.department-tree-panel :deep(.el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content) {
  background: #e0f2fe;
  color: #0369a1;
}

.department-tree-node {
  display: flex;
  align-items: center;
  min-height: 34px;
  width: 100%;
}

.department-tree-node-label {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.desktop-personnel-main {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.table-shell .el-table {
  flex: 1;
  min-height: 0;
  height: 100%;
}

/* Ensure the table body takes full height */
.table-shell .el-table__body-wrapper {
  flex: 1;
  min-height: 0;
}

.mobile-personnel-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px;
  display: grid;
  gap: 8px;
  align-content: start;
}

.mobile-personnel-head {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 8px;
}

.mobile-personnel-head-cell {
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}

.mobile-personnel-head-name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.mobile-personnel-empty {
  border: 1px dashed #cfe1da;
  border-radius: 10px;
  padding: 16px;
  color: #64748b;
  text-align: center;
  background: #f8fcfa;
}

.mobile-personnel-item {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  padding: 8px;
  align-items: stretch;
}

.mobile-personnel-name {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.mobile-personnel-summary {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mobile-personnel-nickname {
  color: #0f172a;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  word-break: break-word;
}

.mobile-personnel-status {
  width: fit-content;
  max-width: 100%;
}

.mobile-personnel-actions {
  min-width: 0;
}

.mobile-personnel-actions .op-actions.mobile {
  width: 100%;
  display: grid;
  gap: 6px;
}

.mobile-personnel-actions .op-actions.mobile .btn {
  width: 100%;
  margin: 0;
  padding: 4px 6px;
  line-height: 1.2;
  justify-content: center;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
  margin-top: -1px;
}

.total-count {
  font-size: 14px;
  color: #6b7280;
}

.pagination {
  display: flex;
  align-items: center;
}

/* Ensure loading overlay works correctly */
@media (max-width: 900px) {
  .desktop-personnel-shell {
    display: block;
    padding: 0;
    background: transparent;
  }

  .page-header {
    align-items: flex-start;
  }

  .page-header-actions {
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
  }

  .batch-ai-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .schedule-dialog-note {
    margin-left: 0;
  }

  .schedule-time-row {
    align-items: stretch;
    flex-direction: column;
  }

  .list-top {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin: 0;
    flex: 1;
  }

  .list-top .el-select,
  .personnel-search-input,
  .search-btn {
    width: 100%;
    min-width: 0;
  }

  .ai-log-main,
  .ai-log-meta {
    align-items: flex-start;
    flex-direction: column;
  }

  .ai-log-dialog {
    min-height: 0;
  }

  .ai-log-list {
    gap: 8px;
  }

  .ai-log-item {
    padding: 10px;
  }

  .ai-log-target {
    font-size: 13px;
    line-height: 1.45;
  }

  .ai-log-meta {
    font-size: 11px;
    gap: 4px;
  }

  .ai-log-empty {
    padding: 14px 10px;
    font-size: 13px;
  }

  .pagination-container {
    padding: 10px 12px;
    gap: 8px;
    flex-wrap: wrap;
  }
}
</style>

